import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys, useGetCollectionQuery } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { type BreadcrumbItem, buildDynamicBreadcrumbs } from '@vizit/utils';
import * as React from 'react';
import { LIBRARY_BREADCRUMB, getLibraryBreadcrumb } from './constants';

const COLLECTION_FALLBACK_NAME = 'Collection';

export function getCollectionHref(workspaceSlug: string, collectionId: string): string {
  return `/w/${workspaceSlug}/library/collections/${collectionId}`;
}

export async function loadCollectionName(
  queryClient: QueryClient,
  { workspaceId, collectionId, page }: { workspaceId: string | null; collectionId: string; page: string },
): Promise<string> {
  if (!workspaceId) return COLLECTION_FALLBACK_NAME;
  try {
    const collection = await queryClient.ensureQueryData({
      queryKey: queryKeys.collections.detail({ workspace_id: workspaceId, collection_id: collectionId }).queryKey,
      queryFn: () => useGetCollectionQuery.fetcher({ workspace_id: workspaceId, collection_id: collectionId })(),
    });
    return collection?.name ?? COLLECTION_FALLBACK_NAME;
  } catch (error) {
    Sentry.captureException(error, {
      level: 'warning',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Navigation,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
      },
      extra: { page },
    });
    return COLLECTION_FALLBACK_NAME;
  }
}

export function buildCollectionChildBreadcrumbs(
  from: { trail: BreadcrumbItem[] } | undefined,
  {
    workspaceSlug,
    collectionId,
    collectionName,
  }: { workspaceSlug: string; collectionId: string; collectionName: string },
  currentLabel: string,
  currentSearch?: Record<string, unknown>,
): BreadcrumbItem[] {
  const trail = from?.trail?.length
    ? from
    : {
        trail: [
          { ...getLibraryBreadcrumb('collections'), search: { view: 'collections' } },
          { label: collectionName, href: getCollectionHref(workspaceSlug, collectionId) },
        ],
      };
  return buildDynamicBreadcrumbs(trail, currentLabel, undefined, currentSearch, LIBRARY_BREADCRUMB);
}

export function withCollectionName(
  breadcrumbs: BreadcrumbItem[],
  collectionHref: string,
  collectionName: string | undefined,
): BreadcrumbItem[] | undefined {
  const index = breadcrumbs.findIndex((crumb) => crumb.href === collectionHref);
  const crumb = breadcrumbs[index];
  if (!collectionName || !crumb || crumb.label === collectionName) return undefined;
  return breadcrumbs.map((item, itemIndex) => (itemIndex === index ? { ...item, label: collectionName } : item));
}

export function useCollectionNamedBreadcrumbs(
  breadcrumbs: BreadcrumbItem[],
  { workspaceSlug, collectionId, workspaceId }: { workspaceSlug: string; collectionId: string; workspaceId?: string },
): BreadcrumbItem[] | undefined {
  const { data: collection } = useGetCollectionQuery(
    { workspace_id: workspaceId ?? '', collection_id: collectionId },
    { enabled: !!workspaceId },
  );
  const collectionHref = getCollectionHref(workspaceSlug, collectionId);
  return React.useMemo(
    () => withCollectionName(breadcrumbs, collectionHref, collection?.name),
    [breadcrumbs, collectionHref, collection?.name],
  );
}
